'use client'

import React, { useState, useCallback, useRef, useEffect, ReactElement } from 'react'
import { Button, WorkflowStepper, ButtonProps } from '@ds/ui'
import { ImportInfo } from '../../types/ImportInfo'
import BannerInfo from '../../types/BannerInfo'
import { apiFetch, handleApiError } from '../../utils/apiClient';

interface ImportWorkflowProps {
  mode: 'namedKey' | 'keyPair'
  controller: 'Home' | 'KeyPair'
  onComplete?: () => void
  onBannerUpdate: (bannerInfo: BannerInfo | null) => void
}

export default function ImportWorkflow({ mode, controller, onComplete, onBannerUpdate }: ImportWorkflowProps) {
  const [activeStep, setActiveStep] = useState(0)
  const [isUploadingFile, setIsUploadingFile] = useState(false)
  const [isUploadingRed, setIsUploadingRed] = useState(false)
  const [isUploadingBlue, setIsUploadingBlue] = useState(false)
  const [isUploadingWhole, setIsUploadingWhole] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [uploadedFiles, setUploadedFiles] = useState<{
    file?: { id: string; name: string }
    keyred?: { id: string; name: string }
    keyblue?: { id: string; name: string }
  }>({})
  const [importInfo, setImportInfo] = useState<ImportInfo | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (fileInputRef.current) {
      fileInputRef.current.accept = '.jec,.json'
    }
  }, []);

  const fetchImportInfo = useCallback(async () => {
    const response = await apiFetch(`Admin/${controller}/GetImportInfo`, {
      method: 'GET'
    });
    if (!response.ok) {
      await handleApiError(response);
    }
    const result = await response.json();
    const importInfo = result.importInfo;
    setImportInfo(importInfo);
  }, [controller])

  useEffect(() => {
    fetchImportInfo();
  }, [fetchImportInfo])

  const handleUpload = useCallback(async (type: 'file' | 'keyred' | 'keyblue', file: File) => {
    try {
      if (type === 'file') {
        setIsUploadingFile(true)
      } else if (type === 'keyred') {
        setIsUploadingRed(true)
      } else if (type === 'keyblue') {
        setIsUploadingBlue(true)
      }
      setError(null)

      // Validate file type
      const validExtensions = ['.jec', '.json']
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase()
      if (!validExtensions.includes(fileExtension)) {
        throw new Error('Invalid file type. Please upload a .jec or .json file.')
      }

      const formData = new FormData()
      formData.append('file', file)
      formData.append('filetype', type)
      formData.append('enctype', 'multipart/form-data')

      const response = await apiFetch(`Admin/${controller}/ImportFile`, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json',
          'enctype': 'multipart/form-data'
        },
      })

      if (!response.ok) {
        await handleApiError(response);
      }

      fetchImportInfo();
      const importInfoRes = importInfo;
      // Update uploadedFiles state based on the type
      setUploadedFiles(prev => ({
        ...prev,
        [type]: {
          id: type === 'file' ? importInfoRes?.filehash : 
               type === 'keyred' ? importInfoRes?.redKey?.id :
               importInfoRes?.blueKey?.id,
          name: file.name
        }
      }))
      onBannerUpdate({ 
        type: 'success', 
        message: `${getFileTypeDisplayName(type)} uploaded successfully` 
      });
      setActiveStep(prevStep => prevStep + 1)
    } catch (err) {
      console.error('Error uploading file:', err)
      onBannerUpdate({ 
        type: 'danger', 
        message: `Error uploading ${getFileTypeDisplayName(type)}: ${err instanceof Error ? err.message : 'An error occurred while uploading the file.'}`,
        retryAction: undefined
      });
    } finally {
      if (type === 'file') {
        setIsUploadingFile(false)
      } else if (type === 'keyred') {
        setIsUploadingRed(false)
      } else if (type === 'keyblue') {
        setIsUploadingBlue(false)
      }
    }
  }, [controller])

  const getFileTypeDisplayName = (type: 'file' | 'keyred' | 'keyblue'): string => {
    switch (type) {
      case 'keyred':
        return 'Red Key';
      case 'keyblue':
        return 'Blue Key';
      case 'file':
        return 'File';
      default:
        return type;
    }
  };

  const handleImport = useCallback(async (buttonTag: string) => { 
    try {
      if (buttonTag === 'Complete') {
        setIsUploadingWhole(true)
      } else if (buttonTag === 'Cancel') {
        setIsUploadingWhole(false)
      }

      const formData = new FormData()
      formData.append('tag', controller)
      formData.append('button', buttonTag)
      formData.append('enctype', 'multipart/form-data')

      const response = await apiFetch(`Admin/${controller}/ImportDone`, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json',
          'enctype': 'multipart/form-data'
        }
      })

      if (!response.ok) {
        await handleApiError(response);
      }
      
      const data = await response.json()
      
      try {
        const messageData = JSON.parse(data.message);
        if (messageData.Error) {
          throw new Error(messageData.Error);
        }
      } catch (parseError) {
        // If message isn't JSON or doesn't have Error field, check data.error
        if (data.error) {
          throw new Error("Upload failed: " + data.error);
        }
      }

      onComplete?.()

      setActiveStep(0)
      setUploadedFiles({})
      setImportInfo(null)
      if (buttonTag === 'Complete') {
        onBannerUpdate({ 
          type: 'success', 
          message: `Import completed successfully` 
        });
      }
      if (buttonTag === 'Cancel') {
        onBannerUpdate({ 
          type: 'success', 
          message: `Import cancelled`,
          retryAction: undefined
        });
      }
    } catch (err) {
      console.error('Error completing import:', err)
      onBannerUpdate({ 
        type: 'danger', 
        message: `Error completing import: ${err instanceof Error ? err.message : 'An error occurred while completing the import.'}`,
        retryAction: undefined
      });
    } finally {
      setIsUploadingWhole(false)
    }
  }, [controller, mode, onComplete, onBannerUpdate])

  const handleButtonClick = useCallback(async (type: 'file' | 'keyred' | 'keyblue') => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.jec,.json'
    
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (file) {
        handleUpload(type, file)
      }
    }
    
    input.click()
    
  }, [handleUpload])

  return (
    <div className="max-w-md mx-auto" style={{ maxWidth: '28.125rem' }}>
      <WorkflowStepper>
        <WorkflowStepper.Item
          text="Upload File"
          status={'current'}
          description={importInfo?.filehash ? `File ID ${importInfo?.filehash?.substring(0, 29)}` : "" }
          callToAction={<Button
            kind="secondary"
            onClick={() => handleButtonClick('file')}
            disabled={isUploadingFile }
            text={isUploadingFile ? 'Uploading...' : 'Upload'}
          />}
        >  
          {uploadedFiles.file && (
              <span className="text-sm text-gray-600">
                {/* File ID {importInfo?.filehash || 'Not available'} */}
              </span>
            )}
        </WorkflowStepper.Item>

        <WorkflowStepper.Item
          text="Upload Red Key"
          status={'current'}
          description={importInfo?.redKey?.id ? `Key ID ${importInfo?.redKey?.id?.substring(0, 29)}` : ""}
          callToAction={<Button
            kind="secondary"
            onClick={() => handleButtonClick('keyred')}
            disabled={isUploadingRed }
            text={isUploadingRed ? 'Uploading...' : 'Upload'}
          />}
        >

          {uploadedFiles.keyred && (
            <span className="ml-2 text-sm text-gray-600">
              {/* Key ID {importInfo?.redKey?.Id || 'Not available'} */}
            </span>
          )}
        </WorkflowStepper.Item>

        <WorkflowStepper.Item
          text="Upload Blue Key"
          status={'current'}
          description={importInfo?.blueKey?.id ? `Key ID ${importInfo?.blueKey?.id?.substring(0, 29)}` : ""}
          callToAction={<Button
            kind="secondary"
            onClick={() => handleButtonClick('keyblue')}
            disabled={isUploadingBlue }
            text={isUploadingBlue ? 'Uploading...' : 'Upload'}
          />}
        >

          {uploadedFiles.keyblue && (
            <span className="ml-2 text-sm text-gray-600">
              {/* Key ID {importInfo?.blueKey?.Id || 'Not available'} */}
            </span>
          )}
        </WorkflowStepper.Item>
      </WorkflowStepper>

      {error && (
        <div className="mt-4 text-sm text-red-600">
          {error}
        </div>
      )}

      <div className="mt-6 flex justify-end gap-4">
        <Button
          kind="secondary"
          onClick={() => handleImport('Cancel')}
          text="Cancel"
        />
        <span style={{ marginLeft: '0.75rem' }}></span>
        <Button
          kind="primary"
          onClick={() => handleImport('Complete')}
          disabled={isUploadingWhole}
          text={mode === 'namedKey' ? 'Import keys from file' : 'Import Keys'}
        />
      </div>
    </div>
  )
}